import React, { useState } from 'react';
import { useT } from '@/contexts/I18nContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import type { GridParams, GridOrientation, GridNaming } from '../../utils/projection';

export const DEFAULT_GRID: GridParams = {
  count: 4,
  gapPx: 8,
  padding: { top: 0.02, bottom: 0.02, left: 0.02, right: 0.02 },
  orientation: 'bottom_to_top',
  naming: 'ряд',
};

interface ShelfGridPanelProps {
  armed: boolean;
  params: GridParams;
  onArm: () => void;
  onDisarm: () => void;
  onChange: (p: GridParams) => void;
  error?: string | null;
}

const ShelfGridPanel: React.FC<ShelfGridPanelProps> = ({ armed, params, onArm, onDisarm, onChange, error }) => {
  const { t } = useT();
  const [perSide, setPerSide] = useState(false);

  if (!armed) {
    return (
      <Button type="button" variant="outline" className="w-full" onClick={onArm}>
        {t('zoning.grid.button')}
      </Button>
    );
  }

  const set = (patch: Partial<GridParams>) => onChange({ ...params, ...patch });
  const setPad = (patch: Partial<GridParams['padding']>) => onChange({ ...params, padding: { ...params.padding, ...patch } });
  const pct = (frac: number) => String(Math.round(frac * 100));
  const frac = (v: string) => Math.max(0, Math.min(100, Number(v) || 0)) / 100;

  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{t('zoning.grid.title')}</div>
        <Button type="button" variant="outline" className="h-7 px-2 text-xs" onClick={onDisarm}>
          {t('zoning.grid.cancel')}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="g-count">{t('zoning.grid.count')}</Label>
          <Input id="g-count" type="number" min={1} value={String(params.count)}
            onChange={(e) => set({ count: Math.max(1, Number(e.target.value) || 1) })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="g-gap">{t('zoning.grid.gap')}</Label>
          <Input id="g-gap" type="number" min={0} value={String(params.gapPx)}
            onChange={(e) => set({ gapPx: Math.max(0, Number(e.target.value) || 0) })} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t('zoning.grid.gapHint')}</p>

      {!perSide ? (
        <div className="space-y-1">
          <Label htmlFor="g-pad">{t('zoning.grid.padding')}</Label>
          <Input id="g-pad" type="number" min={0} max={49} value={pct(params.padding.top)}
            onChange={(e) => { const f = frac(e.target.value); onChange({ ...params, padding: { top: f, bottom: f, left: f, right: f } }); }} />
          <button type="button" className="text-xs text-primary underline" onClick={() => setPerSide(true)}>
            {t('zoning.grid.paddingExpand')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>{t('zoning.grid.paddingTop')}</Label>
            <Input type="number" min={0} max={49} value={pct(params.padding.top)} onChange={(e) => setPad({ top: frac(e.target.value) })} /></div>
          <div className="space-y-1"><Label>{t('zoning.grid.paddingBottom')}</Label>
            <Input type="number" min={0} max={49} value={pct(params.padding.bottom)} onChange={(e) => setPad({ bottom: frac(e.target.value) })} /></div>
          <div className="space-y-1"><Label>{t('zoning.grid.paddingLeft')}</Label>
            <Input type="number" min={0} max={49} value={pct(params.padding.left)} onChange={(e) => setPad({ left: frac(e.target.value) })} /></div>
          <div className="space-y-1"><Label>{t('zoning.grid.paddingRight')}</Label>
            <Input type="number" min={0} max={49} value={pct(params.padding.right)} onChange={(e) => setPad({ right: frac(e.target.value) })} /></div>
        </div>
      )}

      <div className="space-y-1">
        <Label htmlFor="g-orient">{t('zoning.grid.orientation')}</Label>
        <Combobox id="g-orient" value={params.orientation}
          onChange={(v) => set({ orientation: v as GridOrientation })}
          options={[
            { value: 'bottom_to_top', label: t('zoning.grid.orientationB2T') },
            { value: 'left_to_right', label: t('zoning.grid.orientationL2R') },
          ]} />
      </div>

      <div className="space-y-1">
        <Label htmlFor="g-naming">{t('zoning.grid.naming')}</Label>
        <Combobox id="g-naming" value={params.naming}
          onChange={(v) => set({ naming: v as GridNaming })}
          options={[
            { value: 'ряд', label: t('zoning.grid.namingRyad') },
            { value: 'полка', label: t('zoning.grid.namingPolka') },
          ]} />
      </div>

      {error && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</div>}
    </div>
  );
};

export default ShelfGridPanel;
